import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, CaretDown, CaretRight, Funnel } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/enterprise/PageHeader';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import AssistantAnswer from './AssistantAnswer';
import { folderOptions, folderPathLabel } from './folderTree';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const EXAMPLES = [
  'C3604 を含む BOM はどれですか',
  '校正に言及している手順書を表示',
  '調達コスト削減 について述べている資料',
  'NBK-008 に関連する図面',
];

export default function DocumentSearchAssistant() {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [opts, setOpts] = useState({ document_types: [], projects: [] });
  const [docType, setDocType] = useState('all');
  const [projectId, setProjectId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [scope, setScope] = useState('all');
  const [folderId, setFolderId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [folders, setFolders] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/dms/folders`, { headers: authHeader() })
      .then((r) => setFolders((r.data || []).filter((f) => f.seed_key !== 'dms_scan_inbox')))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/document-assistant/filters`, { headers: authHeader() })
      .then((r) => setOpts(r.data || { document_types: [], projects: [] }))
      .catch(() => {});
  }, []);

  const ask = useCallback(async (q) => {
    const text = (q ?? question).trim();
    if (!text) { toast.error('質問を入力してください'); return; }
    setLoading(true);
    try {
      const filters = {};
      if (docType !== 'all') filters.document_types = [docType];
      if (projectId !== 'all') filters.project_id = projectId;
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;
      const r = await axios.post(`${API}/api/document-assistant/ask`, {
        question: text, filters, limit: 8,
        previous_answer: result ? (result.narrative || result.answer || result.answer_ja) : null,
        answer_language: result?.language || null,
        scope_type: scope === 'folder' && folderId ? 'folder' : 'all',
        folder_id: scope === 'folder' ? folderId || null : null,
      }, { headers: authHeader() });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '検索に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [question, docType, projectId, dateFrom, dateTo, scope, folderId, result]);

  const filtersActive = docType !== 'all' || projectId !== 'all' || dateFrom || dateTo;

  return (
    <div className="max-w-[1320px]" data-testid="document-search-assistant">
      <PageHeader
        title="AI検索"
        description="質問すると、社内の書類から根拠と出典つきでお答えします。根拠のない推測はしません"
        testid="assistant-page-header"
      />

      {/* 対象（スコープ）は常に見えるところに置く */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="assistant-scope">
        <span className="text-xs text-zinc-500">対象</span>
        {[['all', 'すべての書類から探す'], ['folder', 'このフォルダーから探す']].map(([v, label]) => (
          <button key={v} type="button" onClick={() => { setScope(v); setResult(null); }}
            aria-pressed={scope === v} data-testid={`assistant-scope-${v}`}
            className={`inline-flex items-center rounded-full border px-3 min-h-[32px] text-xs font-medium transition-colors ${
              scope === v ? 'bg-teal-700 text-white border-teal-700'
                : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
            {label}
          </button>
        ))}
        {scope === 'folder' && (
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger className="h-9 w-56 text-sm" aria-label="フォルダーを選ぶ" data-testid="assistant-scope-folder-select">
              <SelectValue placeholder="フォルダーを選択" />
            </SelectTrigger>
            <SelectContent>
              {folderOptions(folders).map((o) => (
                <SelectItem key={o.value} value={o.value} title={o.pathLabel}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <span className="text-xs text-teal-800" data-testid="assistant-scope-active">
          {scope === 'folder'
            ? (folderPathLabel(folders, folderId)
              ? `フォルダー「${folderPathLabel(folders, folderId)}」のみを対象にします`
              : 'フォルダーを選んでください')
            : 'すべての書類を対象にします'}
        </span>
      </div>

      {/* 質問 */}
      <div className="mt-4 rounded-2xl bg-white ring-1 ring-zinc-200 shadow-sm p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={question} onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="例: C3604 を含む BOM はどれですか"
              className="h-12 pl-10 text-base rounded-xl border-transparent bg-zinc-50 ring-1 ring-zinc-200 focus-visible:ring-2 focus-visible:ring-teal-600"
              data-testid="assistant-question-input" />
          </div>
          <Button onClick={() => ask()} disabled={loading}
            className="h-12 px-5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white"
            data-testid="assistant-ask-btn">
            {loading ? '検索中…' : '質問する'}
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {(result?.suggestions || EXAMPLES).map((ex) => (
            <button key={ex} type="button"
              onClick={() => { setQuestion(ex); ask(ex); }}
              data-testid={`assistant-example-${(result?.suggestions || EXAMPLES).indexOf(ex)}`}
              className="rounded-full px-3 min-h-[32px] inline-flex items-center text-xs text-zinc-600 bg-zinc-50 ring-1 ring-zinc-200 hover:bg-teal-50 hover:text-teal-800 hover:ring-teal-200 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none transition-colors duration-200">
              {ex}
            </button>
          ))}
        </div>

        <button type="button" onClick={() => setShowFilters((v) => !v)}
          data-testid="assistant-filters-toggle"
          className="mt-3 inline-flex min-h-[32px] items-center gap-1.5 rounded-md px-1.5 text-xs text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900">
          <Funnel size={13} weight={filtersActive ? 'fill' : 'regular'} />
          絞り込み{filtersActive ? '（適用中）' : ''}
          {showFilters ? <CaretDown size={11} /> : <CaretRight size={11} />}
        </button>

        {showFilters && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="assistant-filters">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-10 text-sm" data-testid="assistant-filter-type">
                <SelectValue placeholder="種類" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">種類: すべて</SelectItem>
                {opts.document_types.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.value}（{t.count}）</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-10 text-sm" data-testid="assistant-filter-project">
                <SelectValue placeholder="プロジェクト" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">プロジェクト: すべて</SelectItem>
                {opts.projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-10 text-sm" data-testid="assistant-filter-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-10 text-sm" data-testid="assistant-filter-date-to" />
          </div>
        )}
      </div>

      {/* 回答 — 結論を最初に。信頼度・診断は後ろ */}
      {result && (
        <div className="mt-6">
          <AssistantAnswer result={result} />
        </div>
      )}
    </div>
  );
}
